import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CalendarClock, XCircle, CalendarPlus } from "lucide-react";
import { SectionLabel } from "@/components/Reveal";
import SchedulePicker from "@/components/SchedulePicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ManageDemo() {
  const { leadId } = useParams();
  const [lead, setLead] = useState(null);
  const [error, setError] = useState(false);
  const [rescheduling, setRescheduling] = useState(false);

  useEffect(() => {
    axios.get(`${API}/leads/demo/${leadId}`).then((r) => setLead(r.data)).catch(() => setError(true));
  }, [leadId]);

  const cancel = async () => {
    try {
      const { data } = await axios.post(`${API}/leads/demo/${leadId}/cancel`);
      setLead(data);
      setRescheduling(false);
      toast.success("Demo cancelled. Slot released.");
    } catch {
      toast.error("Could not cancel. Try again.");
    }
  };

  if (error) {
    return (
      <main data-testid="manage-demo-error" className="flex min-h-screen items-center justify-center px-6 pt-24">
        <p className="font-mono text-sm text-zinc-500">Booking not found. Contact admin@aintelligence.systems.</p>
      </main>
    );
  }
  if (!lead) return <main className="flex min-h-screen items-center justify-center pt-24"><p className="font-mono text-xs text-zinc-500">loading booking…</p></main>;

  return (
    <main data-testid="manage-demo-page" className="grid-bg mx-auto min-h-screen max-w-3xl px-6 pt-36 pb-24">
      <SectionLabel>Manage Your Demo</SectionLabel>
      <h1 className="mt-6 font-heading text-4xl font-bold tracking-tight text-zinc-900 sm:text-5xl">Hi {lead.name.split(" ")[0]}.</h1>

      <div className="mt-10 border border-zinc-900/10 bg-[#FFFFFF] p-8">
        <div className="flex items-center gap-3">
          <CalendarClock size={18} className="text-cyan-600" />
          <p className="label-mono">Current booking</p>
        </div>
        {lead.scheduled_slot ? (
          <p data-testid="current-slot" className="mt-4 border border-cyan-500/40 bg-cyan-500/5 px-5 py-4 font-mono text-sm text-cyan-600">{lead.scheduled_slot} (Pacific Time)</p>
        ) : (
          <p data-testid="no-slot" className="mt-4 text-sm text-zinc-500">No slot booked yet — pick one below and we'll confirm by email.</p>
        )}
        <div className="mt-6 flex flex-wrap gap-3">
          {!rescheduling && (
            <button data-testid="reschedule-btn" onClick={() => setRescheduling(true)} className="btn-primary !px-6 !py-3">
              {lead.scheduled_slot ? "Reschedule" : "Pick a slot"}
            </button>
          )}
          {lead.scheduled_slot && (
            <>
              <a data-testid="manage-ics-link" href={`${API}/leads/demo/${leadId}/calendar.ics`} className="btn-secondary !px-6 !py-3">
                Add to Calendar <CalendarPlus size={14} />
              </a>
              <button data-testid="cancel-demo-btn" onClick={cancel} className="btn-secondary !px-6 !py-3">
                Cancel Demo <XCircle size={14} />
              </button>
            </>
          )}
        </div>
      </div>

      {rescheduling && (
        <SchedulePicker
          leadId={leadId}
          onScheduled={(slot) => { setLead({ ...lead, scheduled_slot: slot }); setRescheduling(false); }}
        />
      )}
    </main>
  );
}
